import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import { GenericCard } from '@/components/molecules/GenericCard';
import { PokemonPicker } from '@/components/molecules/PokemonPicker';
import { SelectedPokemonPanel } from '@/components/molecules/SelectedPokemonPanel';
import { Loader } from '@/components/atoms/Loader';
import { type OwnedPokemon } from '@/services/pokemon';
import { getPendingExpedition, selectExpeditionPokemon } from '@/services/expedition';
import { useCountdown } from '@/hooks/useCountdown';

const RARITY_CONFIG = {
  0: { label: 'Commun', emoji: '⬜', color: '#95a5a6' },
  1: { label: 'Peu Commun', emoji: '🟩', color: '#2ecc71' },
  2: { label: 'Rare', emoji: '🟦', color: '#3498db' },
  3: { label: 'Épique', emoji: '🟪', color: '#9b59b6' },
  4: { label: 'Légendaire', emoji: '🟨', color: '#ffd700' },
} as const;

const ExpiredState = ({ message }: { message: string }) => (
  <main className="bg-page flex min-h-screen items-center justify-center p-5">
    <div className="w-full max-w-md text-center">
      <p className="mb-4 text-5xl">⏳</p>
      <h2 className="text-content font-lexend mb-2 text-xl font-semibold">Expiration</h2>
      <p className="text-content/60 text-sm">{message}</p>
    </div>
  </main>
);

export const PendingExpeditionPage = () => {
  const { pendingExpeditionId } = useParams<{ pendingExpeditionId: string }>();
  const navigate = useNavigate();

  const [selected, setSelected] = useState<OwnedPokemon | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    data: pendingExp,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['pendingExpedition', pendingExpeditionId],
    queryFn: () => getPendingExpedition(pendingExpeditionId!),
    enabled: !!pendingExpeditionId,
    refetchOnWindowFocus: false,
  });

  const countdown = useCountdown(pendingExp?.expiresAt ?? new Date(0).toISOString());

  useEffect(() => {
    if (pendingExp?.status === 'started') {
      navigate('/pokemons/expedition/current', { replace: true });
    }
  }, [pendingExp?.status, navigate]);

  const { mutate: confirm, isPending: isConfirming } = useMutation<
    { expeditionId: string },
    Error,
    string
  >({
    mutationFn: (allyOwnedPokemonId) =>
      selectExpeditionPokemon(pendingExpeditionId!, allyOwnedPokemonId),
    onSuccess: () => {
      navigate('/pokemons/expedition/current', { replace: true });
    },
    onError: (err) => {
      setApiError(err.message);
    },
  });

  if (isLoading) {
    return (
      <main className="bg-page flex min-h-screen items-center justify-center">
        <Loader size={48} color="#f29602" />
      </main>
    );
  }

  if (isError || !pendingExp) {
    return <ExpiredState message="Ce lien d'expédition est invalide ou introuvable." />;
  }

  if (pendingExp.status === 'expired' || countdown.isExpired) {
    return (
      <ExpiredState message="Cette sélection a expiré, retourne sur Discord pour relancer /expedition." />
    );
  }

  if (pendingExp.status === 'started') {
    return (
      <main className="bg-page flex min-h-screen items-center justify-center">
        <Loader size={48} color="#f29602" />
      </main>
    );
  }

  const rarity = RARITY_CONFIG[pendingExp.ticketRarity];

  return (
    <main className="bg-page min-h-screen">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-5">
        <div className="flex items-center justify-between rounded-2xl bg-surface px-5 py-4 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{rarity.emoji}</span>
            <div>
              <p className="text-content/50 text-xs">Ticket d'expédition</p>
              <p className="font-lexend text-base font-semibold" style={{ color: rarity.color }}>
                {rarity.label}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Clock
              size={14}
              className={countdown.remaining < 300 ? 'text-red-500' : 'text-content/40'}
            />
            <span
              className={`font-lexend text-sm font-semibold tabular-nums ${
                countdown.remaining < 300 ? 'text-red-500' : 'text-content/60'
              }`}
            >
              {countdown.formatted}
            </span>
          </div>
        </div>

        <GenericCard
          title="Choisir un Pokémon allié"
          subtitle="Sélectionne le Pokémon qui t'accompagnera en expédition"
        >
          <PokemonPicker
            onSelect={setSelected}
            selectedId={selected?.ownedPokemonId}
            queryKey={`expedition-pending-${pendingExpeditionId}`}
          />

          {apiError && (
            <div className="mt-4 rounded-xl bg-red-500/10 px-4 py-3">
              <p className="text-sm font-medium text-red-600">{apiError}</p>
            </div>
          )}

          <SelectedPokemonPanel
            selected={selected}
            loading={isConfirming}
            onConfirm={() => {
              if (!selected) return;
              setApiError(null);
              confirm(selected.ownedPokemonId);
            }}
            className="mt-4"
          />
        </GenericCard>
      </div>
    </main>
  );
};
